import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL, FILE_URL } from '../constants';
import { useTokens } from '../context/TokenContext';

const FileUploadGallery = ({ field, defaultFiles = [], onChange, debug = false, error = false }) => {
    const { getItem } = useTokens();
    const adminAuthToken = getItem('admin', 'AuthToken');
    const [files, setFiles] = useState(() => {
        try {
            return defaultFiles[0] ? JSON.parse(defaultFiles[0]) : [];
        } catch (e) {
            console.error("Error parsing JSON:", e);
            return [];
        }
    });
    const [errorFile, setErrorFile] = useState(null);
    const [dragging, setDragging] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [uploadingFiles, setUploadingFiles] = useState({});

    useEffect(() => {
            onChange(files, field.type);
    }, [files, field.type]);

    const handleFileChange = async (e) => {
        const selectedFiles = e.target.files;

        for (let i = 0; i < selectedFiles.length; i++) {
            const selectedFile = selectedFiles[i];

            if (selectedFile.size > 5 * 1024 * 1024) {
                setErrorFile('File size exceeds 5 MB');
                return;
            }

            const fileId = Date.now() + Math.random();
            setUploadingFiles((prev) => ({
                ...prev,
                [fileId]: { status: 'uploading', progress: 0 },
            }));

            const formData = new FormData();
            formData.append('file', selectedFile);

            try {
                setErrorFile(null);

                const res = await axios.post(`${API_URL}/media/upload`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Authorization': `Bearer ${adminAuthToken}`,
                    },
                    onUploadProgress: (progressEvent) => {
                        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadingFiles((prev) => ({
                            ...prev,
                            [fileId]: { status: 'uploading', progress },
                        }));
                    },
                });

                if (res.data.status === 'failed' && res.data.error) {
                    setUploadingFiles((prev) => ({
                        ...prev,
                        [fileId]: { status: 'failed', progress: 0, error: res.data.error },
                    }));
                    return;
                }

                setFiles((prevFiles) => [
                    ...prevFiles,
                    {
                        id: res.data.id,
                        url: res.data.file_urls.full,
                        ext: res.data.ext,
                    },
                ]);

                setUploadingFiles((prev) => ({
                    ...prev,
                    [fileId]: { status: 'success', progress: 100 },
                }));
            } catch (err) {
                setUploadingFiles((prev) => ({
                    ...prev,
                    [fileId]: { status: 'failed', progress: 0, error: 'Failed to upload file' },
                }));
            }
        }
    };

    const handleRemove = (fileId) => {
        setFiles((prevFiles) => prevFiles.filter((file) => file.id !== fileId));
    };

    // Handle Drag Start
    const handleDragStart = (e, index) => {
        setDragging(true);
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = "move";
    };

    // Handle Drag Over (allows dropping)
    const handleDragOver = (e) => {
        e.preventDefault();
    };

    // Handle Drop - Rearranging files
    const handleDrop = (e, index) => {
        e.preventDefault();
        const draggedFile = files[draggedIndex];
        const updatedFiles = [...files];
        updatedFiles.splice(draggedIndex, 1); // Remove the dragged file
        updatedFiles.splice(index, 0, draggedFile); // Insert it at the new position

        setFiles(updatedFiles);
        setDragging(false); // Reset dragging state
    };

    return (
        <div className='d-block w-100'>
            <input type="hidden" id={field.type} name={field.type} value={JSON.stringify(files)} />

            <fieldset
                className="upload_dropZone text-center mb-3 p-4"
                onDragEnter={() => setDragging(true)}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => e.preventDefault()}
            >
                <legend className="visually-hidden">Image uploader</legend>
                <i className="bi bi-image d-inline" style={{ fontSize: '3rem' }}></i>
                <p className="small my-2">
                    Drag &amp; Drop Product media like images and videos inside dashed region
                    <br />
                    <i>or</i>
                </p>
                <input
                    id={`upload_image_background_${field.type}`}
                    className={`${field.type}file custom-file form-control position-absolute invisible`}
                    type="file"
                    multiple
                    accept="image/jpeg, image/png, image/svg+xml"
                    onChange={handleFileChange}
                />
                <label className="btn btn-upload mb-3" htmlFor={`upload_image_background_${field.type}`}>
                    Choose file(s)
                </label>
            </fieldset>

            <div className="gallery">
                {files.map((file, index) => (
                    <div
                        key={file.id}
                        className="gallery-item"
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index)}
                    >
                        <div className="file-preview-wrapper">
                            <img
                                className="filepreview"
                                src={`${FILE_URL}${file.url}`}
                                alt="Preview"
                                style={{ maxWidth: '100px', height: '100px' }}
                            />
                            {uploadingFiles[file.id] && uploadingFiles[file.id].status === 'uploading' && (
                                <div className="overlay">
                                    <div className="spinner"></div>
                                    <div
                                        className="progress-bar"
                                        style={{ width: `${uploadingFiles[file.id].progress}%` }}
                                    ></div>
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() => handleRemove(file.id)}
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                        {uploadingFiles[file.id] && (
                            <div className="upload-status">
                                {uploadingFiles[file.id].status === 'uploading' && (
                                    <span className="text-warning">
                                        Uploading: {uploadingFiles[file.id].progress}%
                                    </span>
                                )}
                                {uploadingFiles[file.id].status === 'failed' && (
                                    <span className="text-danger">Failed: {uploadingFiles[file.id].error}</span>
                                )}
                                {uploadingFiles[file.id].status === 'success' && (
                                    <span className="text-success">Upload Successful</span>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {errorFile && <span className="text-danger small">{errorFile}</span>}
            {debug && !files.length && !errorFile && <span className="text-info small">Debugging Mode</span>}
        </div>
    );
};

export default FileUploadGallery;
